import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Collapse, Empty, Modal, Space } from 'antd';
import { useState } from 'react';
import aiAnalysisService from '../../../services/aiAnalysis.service';
import AiGenerationForm from './AiGenerationForm';
import AiGenerationStatus from './AiGenerationStatus';
import AiDetailedAnalysisView from './AiDetailedAnalysisView';
import AiSummaryAnalysisView from './AiSummaryAnalysisView';
import AiImageReview from './AiImageReview';
import AiArtifactEditor from './AiArtifactEditor';
import AiArtifactDecisionBar from './AiArtifactDecisionBar';
import AiArtifactComparison from './AiArtifactComparison';
import type { AiImagePayload, AiTextPayload } from '../../../types/ai-analysis.types';

export default function AiAnalysisWorkspace({ surveyId }: { surveyId: string }) {
  const queryClient = useQueryClient();
  const [artifactId, setArtifactId] = useState<string>();
  const query = useQuery({
    queryKey: ['ai-generation-requests', surveyId],
    queryFn: () => aiAnalysisService.listRequests(surveyId),
    refetchInterval: ({ state }) =>
      state.data?.some((request) => request.status === 'ACTIVE') ? 5000 : false,
  });
  const artifactQuery = useQuery({
    queryKey: ['ai-artifact', artifactId],
    queryFn: () => aiAnalysisService.getArtifact(artifactId!),
    enabled: Boolean(artifactId),
  });
  const artifact = artifactQuery.data;
  const latestRevision = artifact?.revisions?.[artifact.revisions.length - 1];
  return (
    <Card title="Análises e imagem por IA" style={{ marginTop: 16 }}>
      <Collapse
        items={[
          {
            key: 'new',
            label: 'Nova geração',
            children: <AiGenerationForm surveyId={surveyId} onCreated={() => query.refetch()} />,
          },
        ]}
      />
      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
        {query.data?.length ? (
          query.data.map((request) => (
            <AiGenerationStatus
              key={request.id}
              request={request}
              onCancel={async () => {
                await aiAnalysisService.cancelRequest(request.id);
                query.refetch();
              }}
              onOpenArtifact={setArtifactId}
            />
          ))
        ) : (
          <Empty description="Nenhuma geração solicitada" />
        )}
      </Space>
      <Modal
        width={1000}
        open={Boolean(artifactId)}
        onCancel={() => setArtifactId(undefined)}
        footer={null}
        title="Revisão humana obrigatória"
      >
        {artifact && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {artifact.artifactType === 'DETAILED_ANALYSIS' && (
              <AiDetailedAnalysisView
                payload={artifact.normalizedPayload as AiTextPayload}
                warnings={artifact.warnings}
              />
            )}
            {artifact.artifactType === 'SUMMARY_ANALYSIS' && (
              <AiSummaryAnalysisView payload={artifact.normalizedPayload as AiTextPayload} />
            )}
            {artifact.artifactType === 'ASSOCIATIVE_IMAGE' && (
              <AiImageReview
                payload={artifact.normalizedPayload as AiImagePayload}
                imageUrl={artifact.imageUrl}
              />
            )}
            {artifact.artifactType !== 'ASSOCIATIVE_IMAGE' && (
              <AiArtifactEditor
                initialValue={
                  latestRevision?.sourceText ?? JSON.stringify(artifact.normalizedPayload, null, 2)
                }
                onSave={async (sourceText) => {
                  await aiAnalysisService.createRevision(
                    artifact.id,
                    sourceText,
                    artifact.normalizedPayload
                  );
                  artifactQuery.refetch();
                }}
              />
            )}
            <AiArtifactComparison artifacts={[artifact]} />
            <AiArtifactDecisionBar
              approved={artifact.reviewStatus === 'APPROVED'}
              stale={Boolean(artifact.staleAt)}
              artifactType={artifact.artifactType}
              onApprove={async () => {
                await aiAnalysisService.decide(artifact.id, 'APPROVE', {
                  revisionId: latestRevision?.id,
                });
                artifactQuery.refetch();
              }}
              onReject={async () => {
                await aiAnalysisService.decide(artifact.id, 'REJECT', {
                  revisionId: latestRevision?.id,
                });
                artifactQuery.refetch();
              }}
              onApply={async (confirmStale) => {
                await aiAnalysisService.decide(artifact.id, 'APPLY', {
                  revisionId: latestRevision?.id,
                  confirmStale,
                });
                artifactQuery.refetch();
                queryClient.invalidateQueries({
                  queryKey: ['survey-analysis-complementary', Number(surveyId)],
                });
              }}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
}
